import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnVisibilityState as VisibilityState } from '@tanstack/react-table'
import { useMemo } from 'react'

import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import type { TablePreferences } from '@/types/models'

/** `DataTable`'ın beklediği TanStack biçimi (API snake_case taşır). */
export interface TableLayout {
  columnOrder: string[]
  columnSizing: Record<string, number>
  columnVisibility: VisibilityState
}

const EMPTY: TablePreferences = { column_order: [], column_sizing: {}, column_visibility: {} }

/**
 * Kullanıcının tablo düzeni sunucuda tutulur (cihazdan bağımsız). Yazma iyimserdir:
 * önbellek hemen güncellenir, istek başarısız olursa yalnızca loglanır — tablo kullanımı
 * kesilmez. Aynı tabloya giden yazmalar `scope` ile sıraya dizilir ki eski bir PUT yenisini ezmesin.
 */
export function useTablePreferences(tableId: string, defaultVisibility: VisibilityState) {
  const queryClient = useQueryClient()
  const key = queryKeys.tablePreferences(tableId)

  const { data = EMPTY } = useQuery({
    queryKey: key,
    queryFn: () => services.preferences.getTable(tableId),
    staleTime: Infinity,
  })

  const write = async (next: TablePreferences) => {
    await queryClient.cancelQueries({ queryKey: key })
    queryClient.setQueryData(key, next)
  }
  const onError = (error: unknown) => console.warn(`Tablo tercihi kaydedilemedi: ${tableId}`, error)
  const scope = { id: key.join('/') }

  const save = useMutation({
    scope,
    mutationFn: (next: TablePreferences) => services.preferences.saveTable(tableId, next),
    onMutate: write,
    onError,
  })
  const reset = useMutation({
    scope,
    mutationFn: () => services.preferences.resetTable(tableId),
    onMutate: () => write(EMPTY),
    onError,
  })

  const layout: TableLayout = useMemo(
    () => ({
      columnOrder: data.column_order,
      columnSizing: data.column_sizing,
      // Boş = kullanıcı hiç dokunmamış; view'ın varsayılan gizli kolonları geçerli.
      columnVisibility: Object.keys(data.column_visibility).length
        ? data.column_visibility
        : defaultVisibility,
    }),
    [data, defaultVisibility],
  )

  return {
    layout,
    save: (next: TableLayout) =>
      save.mutate({
        column_order: next.columnOrder,
        column_sizing: next.columnSizing,
        column_visibility: next.columnVisibility,
      }),
    reset: () => reset.mutate(),
  }
}
